import React, { useEffect, useRef, useState } from 'react'
import { i18n } from '@/locale/i18n'
import { useNavigate } from 'react-router-dom'
import classnames from 'classnames'
import './FilterDropdownPanel.scss'
import { useDispatch, useSelector } from 'react-redux'
import { selectMainRootCategoryList } from '@/store/categories'
import { selectAbTestsStatus, selectActiveCurrency, setMarketType, storeActiveCurrency } from '@/store/app'
import constants from '@/constants'
import { useABTests, useCurrencies, useCurrentUser } from '@/hooks'
import ActionSheetLayout from '@/components/action-sheet-layout'
import { DotIcon, DotsIcon, FiltersIcon } from '../../assets/icons'
import Button from '@/components/ui/button'
import { urlSearchParams } from '@/util/urlSearchParams'
import useIsMobileSize from '@/hooks/useIsMobileSize'
import { Sheet } from 'react-modal-sheet'
import CurrencyIcon from '@/components/currencyIcon'
import MenuItem from '@/components/dropdown-menu/MenuItem'
import Dropdown from '@/components/ui/dropdown'
import Modal from '@/layouts/modal/Modal.jsx'
import { pathnameWithoutLocalePrefix } from '@/util/localePath'
import { defaultFeedOrdering } from '@/constants/feedOrdering'

/* The panel has no router hook of its own and reads the path off the global `location`,
   the way its category check below does — there is none while server-rendering. */
const currentPathname = () => (typeof location !== 'undefined' ? location.pathname : '')

const FilterDropdownPanel = ({
  className = '',
  containerClass = '',
  queryParams = {},
  onChange,
  filterOf = 'all',
  page = 'feed',
  setOnChangeFilters = [],
  statusList = [],
  currencies,
  ...props
}) => {
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const currentUser = useCurrentUser()
  const _currencies = useCurrencies().currencies
  const categories = useSelector(selectMainRootCategoryList)

  const [activeCurrencyMode, setLocalActiveCurrencyMode] = useState(
    useSelector(selectActiveCurrency) == 'OOM' ? constants.PLAY_MONEY : constants.REAL_MONEY
  )
  const isMobileSize = useIsMobileSize()

  const abTestsStatus = useSelector(selectAbTestsStatus)
  const { getABTestSegment } = useABTests()

  const [categoriesSelected, setCategoriesSelected] = useState(props.categoriesSelected)
  const [currenciesSelected, setCurrenciesSelected] = useState(props.currenciesSelected)
  const [marketStatusSelected, setMarketStatusSelected] = useState(props.marketStatusSelected)
  const [sideSelected, setSideSelected] = useState(props.sideSelected)
  const [modalOpen, setModalOpen] = useState(false)
  const flatControls = isMobileSize

  // On mobile feed/markets, filter changes are staged here and only committed on "Apply".
  // (Portfolio already defers via its own Apply handler.)
  const deferToApply = isMobileSize && page !== 'portfolio' && page !== 'portfolio-limit-orders'
  // Desktop feed/markets: debounce the apply so users can pick several filters before it fetches.
  const debounceOnDesktop = !isMobileSize && page !== 'portfolio' && page !== 'portfolio-limit-orders'
  const [pendingParams, setPendingParams] = useState(null)
  const desktopApplyTimer = useRef(null)
  // Params used to read the active state of each control.
  // Mobile: only while the modal is open. Desktop: while a debounced apply is pending.
  const usePending = deferToApply ? modalOpen : true
  const effectiveParams = usePending && pendingParams ? pendingParams : queryParams

  useEffect(() => {
    return () => {
      if (desktopApplyTimer.current) clearTimeout(desktopApplyTimer.current)
    }
  }, [])

  // Portfolio: default the currency filter to all currencies checked (once, when available).
  const didInitCurrencies = useRef(false)
  useEffect(() => {
    if (didInitCurrencies.current) return
    if (page !== 'portfolio' && page !== 'portfolio-limit-orders') return
    if (currenciesSelected != null) return
    const all = (currencies || []).filter(c => c && c !== constants.REAL_MONEY_PLACEHOLDER)
    if (all.length === 0) return
    didInitCurrencies.current = true
    setCurrenciesSelected(all)
  }, [page, currencies, currenciesSelected])

  const sortByOptions = [
    { value: '-created_on', text: i18n('markets.most_recent') },
    { value: 'bet_end_date', text: i18n('markets.bet_end_date') },
    { value: '-wagers_count', text: i18n('markets.number_of_bets') },
    { value: '-volume', text: i18n('markets.volume') },
    { value: '-relevance', text: i18n('markets.relevance') },
  ]
  useEffect(() => setMarketStatusSelected(props.marketStatusSelected), [props.marketStatusSelected])

  // Portfolio: visually default the status filter to all statuses checked. Computed at render
  // time (like getCatagoryValue below) instead of a one-time effect, so it can't race with
  // per-tab remounts. status_group already returns every active status, so this stays a
  // UI-only default and is not pushed into the data layer (which would send a redundant status
  // filter on every fetch) — only real user selections do, via the normal onChange path.
  const getMarketStatusValue = () => {
    if (page !== 'portfolio') return marketStatusSelected
    const all = (statusList || []).map(s => s.value).filter(v => v != null)
    return !marketStatusSelected || marketStatusSelected.length === 0 ? all : marketStatusSelected
  }

  useEffect(() => {
    /**
     * An async function to get query parameters from the URL,
     * and set a default ordering if not present.
     */
    const getQueries = () => {
      const _queryParams = Object.fromEntries(new urlSearchParams(location.search))

      const feedDefaultSort = getABTestSegment('feed-sort') || ''

      // Only a real segment names an ordering: assigning the empty fallback
      // put `ordering=` on the questions request and on the shared params
      // object every other control reads.
      if (!_queryParams.ordering && feedDefaultSort) {
        _queryParams.ordering = feedDefaultSort
      }

      return _queryParams
    }

    if (abTestsStatus === constants.COMPLETE) {
      const query = getQueries()
      if (query.ordering) queryParams.ordering = query.ordering
    }
  }, [location.search, abTestsStatus])

  const categorieOptions = categories
    .filter(cat => cat !== undefined && cat.id !== 1)
    .map(cat => ({ value: cat.id + '', text: cat.title }))

  const currencySelector = [
    {
      value: constants.REAL_MONEY,
      text: i18n('Real_money'),
    },
    {
      value: constants.PLAY_MONEY,
      text: i18n('Play_money'),
    },
  ]

  if (page === 'portfolio') {
    queryParams.currency_mode = activeCurrencyMode === 'OOM' ? constants.PLAY_MONEY : constants.REAL_MONEY
  }
  const getCatagoryValue = () => {
    const rawCat = effectiveParams.categories
    const splitCat = Array.isArray(rawCat) ? rawCat : rawCat ? rawCat.split(',') : null
    const selectedCat = categoriesSelected || splitCat
    // Default to all categories when nothing is selected
    return !selectedCat || selectedCat.length === 0 ? categorieOptions.map(c => c.value) : selectedCat
  }

  const changeSearchFilter = (filter, value) => {
    // While staging (mobile defer) or debouncing (desktop), accumulate from the staged params
    // so consecutive selections build on each other; otherwise read from the URL.
    const source = deferToApply || debounceOnDesktop ? pendingParams || queryParams : queryParams
    // Start from everything the URL already carries — the topic refinement
    // (`tag`), the featured topic (`topic`), a search term, and any param this
    // panel does not own — so applying one filter never silently discards
    // another. The whitelist this used to rebuild from dropped `tag`, which
    // reset the feed to "All" the moment a category or status was touched.
    const params = { ...source }

    value ? (params[filter] = value) : delete params[filter]

    // The featured-topic refinement only exists on the volume ("All") view;
    // picking any other ordering leaves that view, so the topic goes with it
    // instead of lingering as an invisible filter on the new ordering.
    if (filter === 'ordering' && value !== '-volume') delete params.topic

    // Inverted filters : one cancels the other
    if (filter === 'live_soon' || filter === 'live' || filter === 'all') {
      delete params.resolved_only
      delete params.resolved_and_sold
      delete params.pending_resolution
    }
    if (filter === 'resolved_only' || filter === 'resolved_and_sold' || filter === 'all') {
      delete params.live_soon
      delete params.live
      delete params.pending_resolution
    }
    if (filter === 'pending_resolution' || filter === 'all') {
      delete params.live_soon
      delete params.live
      delete params.resolved_only
      delete params.resolved_and_sold
    }
    if (!params.categories || params.categories?.length <= 0) delete params.categories

    // Mobile feed/markets: stage the change, commit only on Apply.
    if (deferToApply) {
      setPendingParams(params)
      return
    }

    // Desktop feed/markets: update the visual immediately, debounce the navigate by 500ms
    // so picking several filters in a row triggers a single fetch.
    if (debounceOnDesktop) {
      setPendingParams(params)
      if (desktopApplyTimer.current) clearTimeout(desktopApplyTimer.current)
      desktopApplyTimer.current = setTimeout(() => {
        navigate(location.pathname + '?' + new URLSearchParams(params).toString())
        if (onChange) onChange()
        setPendingParams(null)
      }, 500)
      return
    }

    if (!isMobileSize && page !== 'portfolio') {
      setOnChangeFilters.forEach(field => {
        field.action(queryParams[field.filter])
      })
    }
    const filterTouched = setOnChangeFilters.find(field => field.filter === filter)
    if (!isMobileSize && filterTouched && filterTouched.action) {
      filterTouched.action(params[filter])
    }
    if (page !== 'portfolio')
      navigate(location.pathname + '?' + new URLSearchParams(params).toString())
    if (onChange && !isMobileSize) onChange()
  }
  const onChangeFilter = (filter, value) => {
    changeSearchFilter(filter, value)
  }
  const changeCurrencyMode = currency_mode => {
    dispatch(storeActiveCurrency(currency_mode === constants.REAL_MONEY ? constants.CANONICAL_CURRENCY : 'OOM'))
    setLocalActiveCurrencyMode(currency_mode)
    queryParams.currency_mode = currency_mode
    dispatch(setMarketType(currency_mode))
  }

  const shouldShowFilter = filter => {
    const result = (() => {
      switch (filter) {
        case 'sort_by':
          return ['all', 'markets'].includes(filterOf) && page !== 'portfolio' && page !== 'portfolio-limit-orders'
        case 'currencies':
          return page === 'portfolio' || page === 'portfolio-limit-orders'
        case 'currency_mode':
          return false
        case 'default_category': {
          // Categories filter only on the main feed views (Trending `/`, New & All `/markets`),
          // not on a specific category page (`/markets/{slug}`) or tag page.
          if (page === 'portfolio' || page === 'portfolio-limit-orders') return false
          if (!['all', 'markets'].includes(filterOf)) return false
          const cleanPath =
            typeof location !== 'undefined' ? pathnameWithoutLocalePrefix(location.pathname).replace(/\/$/, '') : ''
          return cleanPath === '' || cleanPath === '/markets'
        }
        case 'live_markets':
          return false //['all', 'markets'].includes(filterOf) && page === 'feed'
        case 'live':
          return false //page === 'profile'
        case 'resolved_and_sold':
          return false //page === 'profile'
        case 'market_status':
          return page === 'portfolio' && page !== 'portfolio-limit-orders'
        case 'categories':
          return page === 'portfolio' || page === 'portfolio-limit-orders'
        case 'side':
          return page === 'portfolio-limit-orders'
        case 'resolved_only':
          return ['all', 'markets'].includes(filterOf) && page !== 'portfolio' && page !== 'portfolio-limit-orders'
        case 'all':
          return ['all', 'markets'].includes(filterOf) && page !== 'portfolio' && page !== 'portfolio-limit-orders'
        case 'hide_my_bets':
          return false
        case 'people_i_follow':
          return ['users'].includes(filterOf)
        case 'ordering':
          return page !== 'search'
        case 'filter_by':
          return ['all', 'markets'].includes(filterOf) && page !== 'portfolio' && page !== 'portfolio-limit-orders'
        case 'only_markets_i_follow':
          return page !== 'portfolio' && page !== 'portfolio-limit-orders'
        case 'more_options':
          return ['all', 'markets'].includes(filterOf) && page !== 'portfolio' && page !== 'portfolio-limit-orders'
        default:
          return true
      }
    })()

    return result
  }
  const showModal = () => {
    // Snapshot current params so mobile feed/markets edits are staged until "Apply".
    if (deferToApply) setPendingParams({ ...queryParams })
    setModalOpen(true)
  }

  const hideModal = () => {
    setModalOpen(false)
  }

  // Commit staged params (mobile feed/markets) on "Apply".
  const applyPendingParams = () => {
    if (!deferToApply || !pendingParams) return
    const qs = new URLSearchParams(pendingParams).toString()
    navigate(location.pathname + (qs ? `?${qs}` : ''))
    if (onChange) onChange()
  }

  // Reset all filters back to default.
  const resetFilters = () => {
    setCategoriesSelected(null)
    setMarketStatusSelected(null)
    setSideSelected(null)

    if (page === 'portfolio' || page === 'portfolio-limit-orders') {
      // Portfolio default = all currencies checked
      const allCurrencies = (currencies || []).filter(c => c && c !== constants.REAL_MONEY_PLACEHOLDER)
      setCurrenciesSelected(allCurrencies)
      setOnChangeFilters.forEach(f => {
        if (f.filter === 'currencies') f.action(allCurrencies)
        else f.action(undefined)
      })
    } else {
      setCurrenciesSelected(null)
      // Feed/markets: clear filters (preserve free-text search, its topic
      // refinement, and ordering — those are the view being filtered, not
      // filters themselves)
      const params = {}
      if (queryParams.q) params.q = queryParams.q
      if (queryParams.search) params.search = queryParams.search
      if (queryParams.ordering) params.ordering = queryParams.ordering
      if (queryParams.tag) params.tag = queryParams.tag
      if (queryParams.topic) params.topic = queryParams.topic
      if (deferToApply) {
        // Stage the cleared state; commit on Apply
        setPendingParams(params)
      } else {
        const qs = new URLSearchParams(params).toString()
        navigate(location.pathname + (qs ? `?${qs}` : ''))
      }
    }
  }

  const render = () => {
    return (
      <ul className={classnames('filter-dropdown-panel', 'flex-col md:flex-row', className)}>
        {shouldShowFilter('currencies') && (
          <li>
            <Dropdown
              dropdownType="list"
              type="outline"
              multiple
              bulkActions
              title={i18n('filters.currencies')}
              placeholder={i18n('portfolio.all_currencies')}
              value={currenciesSelected}
              list={
                _currencies &&
                currencies
                  .map(c => [c, _currencies[c]])
                  .map(([currencyId, currency]) => ({
                    value: currencyId,
                    text: currencyId === 'OOM' ? i18n('Play_money') : currencyId,
                    icon: <CurrencyIcon currency={currency} width="20px" />,
                  }))
                  .filter(({ value }) => value !== constants.REAL_MONEY_PLACEHOLDER)
              }
              onChange={value => {
                setCurrenciesSelected(value.length > 0 ? value : null)
                onChangeFilter('currencies', value)
              }}
              flat={flatControls}
            />
          </li>
        )}
        {shouldShowFilter('market_status') && (
          <li>
            <Dropdown
              dropdownType="list"
              type="outline"
              multiple
              bulkActions
              title={i18n('filters.status')}
              placeholder={i18n('filters.status')}
              value={getMarketStatusValue()}
              list={statusList}
              onChange={value => {
                setMarketStatusSelected(value.length > 0 ? value : null)
                onChangeFilter('market_status', value)
              }}
              flat={flatControls}
              showOptionsCount={true}
            />
          </li>
        )}
        {shouldShowFilter('categories') && (
          <li className="w-full md:w-fit">
            <Dropdown
              dropdownType="list"
              type="outline"
              multiple
              bulkActions
              placeholder={i18n('filters.categories')}
              title={i18n('filters.categories')}
              value={getCatagoryValue()}
              list={categorieOptions}
              onChange={(newArray, lastSelected) => {
                const valuesToAffect =
                  lastSelected === ''
                    ? newArray.indexOf('') >= 0
                      ? categorieOptions.map(c => c.value)
                      : newArray
                    : newArray.filter(c => c !== '')

                // If user tries to deselect all, select all instead
                const finalValue = valuesToAffect.length === 0 ? categorieOptions.map(c => c.value) : valuesToAffect

                setCategoriesSelected(finalValue)
                onChangeFilter('categories', finalValue)
              }}
              flat={flatControls}
              showOptionsCount={true}
            />
          </li>
        )}
        {shouldShowFilter('side') && (
          <li>
            <Dropdown
              dropdownType="list"
              type="outline"
              title={i18n('filters.trade')}
              placeholder={i18n('filters.trade')}
              value={sideSelected}
              list={props.sideList}
              onChange={value => {
                setSideSelected(value)
                onChangeFilter('side', value)
              }}
              flat={flatControls}
            />
          </li>
        )}
        {shouldShowFilter('currency_mode') && (
          <li>
            <Dropdown
              dropdownType="list"
              type="outline"
              placeholder={i18n('currency_selector.title')}
              value={activeCurrencyMode}
              list={currencySelector}
              onChange={changeCurrencyMode}
              flat={flatControls}
            />
          </li>
        )}
        {shouldShowFilter('filter_by') && (
          <li className="w-full md:w-fit">
            <Dropdown
              dropdownType="list"
              type="outline"
              multiple
              title={i18n('filters.status')}
              placeholder={
                effectiveParams.resolved_only
                  ? i18n('filters.status_closed')
                  : effectiveParams.pending_resolution
                    ? i18n('filters.status_pending')
                    : i18n('filters.status_open')
              }
              custom={
                <>
                  <MenuItem
                    text={i18n('filters.status_open_option')}
                    trailingControl="radio"
                    active={
                      !effectiveParams.resolved_only && !effectiveParams.live && !effectiveParams.pending_resolution
                    }
                    closeMenuOnClick={false}
                    onClick={() => {
                      if (effectiveParams.resolved_only || effectiveParams.live || effectiveParams.pending_resolution)
                        onChangeFilter('all', true)
                    }}
                  />
                  <MenuItem
                    text={i18n('filters.status_pending_option')}
                    trailingControl="radio"
                    active={effectiveParams.pending_resolution}
                    closeMenuOnClick={false}
                    onClick={() => {
                      if (!effectiveParams.pending_resolution) onChangeFilter('pending_resolution', true)
                    }}
                  />
                  <MenuItem
                    text={i18n('filters.status_closed_option')}
                    trailingControl="radio"
                    active={effectiveParams.resolved_only}
                    closeMenuOnClick={false}
                    onClick={() => {
                      if (!effectiveParams.resolved_only) onChangeFilter('resolved_only', true)
                    }}
                  />
                </>
              }
              onChange={value => onChangeFilter('live', value)}
              flat={flatControls}
              showOptionsCount
            />
          </li>
        )}
        {shouldShowFilter('default_category') && (
          <li className="w-full md:w-fit">
            <Dropdown
              dropdownType="list"
              type="outline"
              bulkActions
              placeholder={i18n('filters.categories')}
              title={i18n('filters.categories')}
              value={getCatagoryValue()}
              list={categorieOptions}
              multiple
              onChange={(newArray, lastSelected) => {
                const valuesToAffect =
                  lastSelected === ''
                    ? newArray.indexOf('') >= 0
                      ? categorieOptions.map(c => c.value)
                      : newArray
                    : newArray.filter(c => c !== '')

                // If user tries to deselect all, select all instead
                const finalValue = valuesToAffect.length === 0 ? categorieOptions.map(c => c.value) : valuesToAffect

                setCategoriesSelected(finalValue)
                onChangeFilter('categories', finalValue)
              }}
              flat={flatControls}
              showOptionsCount={true}
            />
          </li>
        )}
        {shouldShowFilter('resolved_and_sold') && (
          <li>
            <Dropdown
              dropdownType="boolean"
              type="outline"
              value={effectiveParams.resolved_and_sold}
              placeholder={i18n('markets.resolved_and_sold')}
              onChange={value => {
                onChangeFilter('resolved_and_sold', value)
              }}
              flat={flatControls}
            />
          </li>
        )}
        {shouldShowFilter('live_markets') && (
          <li>
            <Dropdown
              dropdownType="boolean"
              type="outline"
              placeholder={i18n('markets.live_markets')}
              value={effectiveParams.live}
              onChange={value => onChangeFilter('live', value)}
              flat={flatControls}
            />
          </li>
        )}
        {shouldShowFilter('live') && (
          <li>
            <Dropdown
              dropdownType="boolean"
              type="outline"
              placeholder={i18n('markets.live_markets')}
              value={effectiveParams.live}
              onChange={value => onChangeFilter('live', value)}
              flat={flatControls}
            />
          </li>
        )}
        {shouldShowFilter('hide_my_bets') && (
          <li>
            <Dropdown
              dropdownType="boolean"
              type="outline"
              value={effectiveParams.hide_my_bets}
              placeholder={i18n('filters.hide_my_bets')}
              onChange={value => onChangeFilter('hide_my_bets', value)}
              flat={flatControls}
            />
          </li>
        )}
        {shouldShowFilter('people_i_follow') && (
          <li>
            <Dropdown
              dropdownType="boolean"
              type="outline"
              value={effectiveParams.people_i_follow}
              placeholder={i18n('filters.people_i_follow')}
              onChange={value => onChangeFilter('people_i_follow', value)}
              flat={flatControls}
            />
          </li>
        )}
        {!isMobileSize && shouldShowFilter('sort_by') && (
          <li>
            <Dropdown
              dropdownType="list"
              type="outline"
              title={i18n('markets.sort_by')}
              keepPlaceholder={true}
              value={queryParams.ordering || defaultFeedOrdering(currentPathname())}
              list={sortByOptions}
              arrow="outlined"
              highlightSelected
              onChange={value => onChangeFilter('ordering', value)}
              flat={flatControls}
            />
          </li>
        )}
        {currentUser && shouldShowFilter('more_options') && (
          <li className="w-full md:w-fit">
            <Dropdown
              dropdownType="list"
              type="outline"
              inputClass="!px-3"
              showArrow={false}
              title={i18n('filters.others')}
              ariaLabel={i18n('filters.others')}
              placeholder={
                <DotsIcon
                  fillcolor="var(--color-Neutral-6)"
                  width="20"
                  height="20"
                  style={{ transform: 'rotate(90deg)' }}
                />
              }
              custom={
                <>
                  <MenuItem
                    text={i18n('filters.hide_my_bets')}
                    trailingControl="toggle"
                    active={effectiveParams.hide_my_bets}
                    closeMenuOnClick={false}
                    onClick={e => e?.stopPropagation?.()}
                    onToggle={() => onChangeFilter('hide_my_bets', !effectiveParams.hide_my_bets)}
                  />
                  <MenuItem
                    text={i18n('filters.market_i_follow')}
                    trailingControl="toggle"
                    active={effectiveParams.only_markets_i_follow}
                    closeMenuOnClick={false}
                    onClick={e => e?.stopPropagation?.()}
                    onToggle={() => onChangeFilter('only_markets_i_follow', !effectiveParams.only_markets_i_follow)}
                  />
                </>
              }
              onChange={() => {}}
              flat={flatControls}
            />
          </li>
        )}
        {isMobileSize && <li className="pb-2"></li>} {/* small breathing room before the sticky footer */}
      </ul>
    )
  }

  return isMobileSize ? (
    <>
      <div
        onClick={showModal}
        className="rounded-lg relative flex w-10 h-10 items-center justify-center bg-surface-2 border border-solid border-Neutral-3">
        <FiltersIcon width="13px" fill="var(--color-Neutral-6)" />
        {(currenciesSelected || marketStatusSelected || categoriesSelected || sideSelected) && (
          <i className="absolute right-0 bottom-0">
            <DotIcon color="var(--color-brand-04)" />
          </i>
        )}
      </div>

      <Modal
        isOpen={modalOpen}
        position="bottom"
        onCloseRequested={() => hideModal()}
        onClose={() => hideModal()}
        showCloseButton
        headerClassList="pt-6 pl-6 pr-6 ml-0 mr-0"
        containerClass={`${containerClass} filter-modal__content pl-6 pr-6 ml-0 mr-0 max-h-[80vh]`}
        title={i18n('filters.filter_by')}
        content={render()}
        footer={
          <div className="w-full p-4 bg-surface-2 shrink-0 flex gap-3">
            <Button
              size="full"
              type="text-only"
              onClick={() => {
                resetFilters()
              }}>
              {i18n('filters.reset')}
            </Button>
            <Button
              size="full"
              onClick={() => {
                if (page === 'portfolio') {
                  setOnChangeFilters.find(f => f.filter === 'currencies').action(currenciesSelected)
                  setOnChangeFilters.find(f => f.filter === 'categories').action(categoriesSelected)
                  setOnChangeFilters.find(f => f.filter === 'market_status').action(marketStatusSelected)
                } else if (page === 'portfolio-limit-orders') {
                  const categoriesFilter = setOnChangeFilters.find(f => f.filter === 'categories')
                  const currenciesFilter = setOnChangeFilters.find(f => f.filter === 'currencies')
                  const sideFilter = setOnChangeFilters.find(f => f.filter === 'side')

                  if (categoriesFilter) categoriesFilter.action(categoriesSelected)
                  if (currenciesFilter) currenciesFilter.action(currenciesSelected)
                  if (sideFilter) sideFilter.action(sideSelected)
                } else {
                  // Feed/markets: commit the staged filter changes
                  applyPendingParams()
                }
                hideModal()
              }}>
              {i18n('filters.apply')}
            </Button>
          </div>
        }></Modal>
    </>
  ) : (
    render()
  )
}

export default FilterDropdownPanel
